import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTicketAlt, 
  faSpinner,
  faFilter,
  faSearch,
  faUserShield,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import './AdminDashboard.css';

import UserManagement from './UserManagement';

interface Ticket {
  id: number;
  ticket_number: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  user_id: number;
  user_name: string;
  user_email: string;
  created_at: string;
  updated_at: string;
}

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('tickets');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Dashboard stats
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    highPriority: 0
  });
  
  // Delete ticket state
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    }
  }, [activeTab]);
  
  const fetchTickets = async () => {
    try {
      const res = await axios.get('/api/tickets/all');
      setTickets(res.data.tickets);
      setFilteredTickets(res.data.tickets);
      setLoading(false);
      
      // Calculate stats
      const ticketData = res.data.tickets;
      setStats({
        total: ticketData.length,
        open: ticketData.filter((ticket: Ticket) => ticket.status === 'open').length,
        inProgress: ticketData.filter((ticket: Ticket) => ticket.status === 'in-progress').length,
        resolved: ticketData.filter((ticket: Ticket) => ticket.status === 'resolved').length,
        closed: ticketData.filter((ticket: Ticket) => ticket.status === 'closed').length,
        highPriority: ticketData.filter((ticket: Ticket) => ticket.priority === 'high').length
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching tickets');
      setLoading(false);
    }
  };
  
  const handleDeleteClick = (ticketId: number) => {
    setDeleteConfirm(ticketId);
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };
  
  const handleDeleteConfirm = async (ticketId: number) => {
    setDeleteLoading(true);
    
    try {
      await axios.delete(`/api/tickets/${ticketId}`);
      
      // Refresh ticket list
      await fetchTickets();
      
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting ticket');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  useEffect(() => {
    // Apply filters and search
    let result = tickets;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(ticket => ticket.status === statusFilter);
    }
    
    // Apply priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(ticket => ticket.priority === priorityFilter);
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        ticket => 
          ticket.subject.toLowerCase().includes(term) ||
          ticket.description.toLowerCase().includes(term) ||
          ticket.user_name.toLowerCase().includes(term) ||
          ticket.user_email.toLowerCase().includes(term) ||
          ticket.id.toString().includes(term)
      );
    }
    
    setFilteredTickets(result);
  }, [tickets, statusFilter, priorityFilter, searchTerm]);
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-danger';
      case 'in-progress':
        return 'bg-warning';
      case 'resolved':
        return 'bg-success';
      case 'closed':
        return 'bg-secondary';
      default:
        return 'bg-primary';
    }
  };
  
  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-danger';
      case 'medium':
        return 'bg-warning';
      case 'low':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FontAwesomeIcon icon={faUserShield} className="me-2" />
          Admin Dashboard
        </h2>
      </div>
      
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
            Ticket Management
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            User Management
          </button>
        </li>
      </ul>
      
      {activeTab === 'tickets' ? (
        <>
          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-2">
              <div className="card text-center shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-1">Total</h5>
                  <h2 className="mb-0">{stats.total}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-danger text-white shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-1">Open</h5>
                  <h2 className="mb-0">{stats.open}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-warning shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-1">In Progress</h5>
                  <h2 className="mb-0">{stats.inProgress}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-success text-white shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-1">Resolved</h5>
                  <h2 className="mb-0">{stats.resolved}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-secondary text-white shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-1">Closed</h5>
                  <h2 className="mb-0">{stats.closed}</h2>
                </div>
              </div>
            </div>
            <div className="col-md-2">
              <div className="card text-center bg-danger text-white shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-1">High Priority</h5>
                  <h2 className="mb-0">{stats.highPriority}</h2>
                </div>
              </div>
            </div>
          </div>
          
          {/* Filters and Search */}
          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3 mb-3 mb-md-0">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faFilter} />
                </span>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faFilter} />
                </span>
                <select
                  className="form-select"
                  value={priorityFilter}
                  onChange={e => setPriorityFilter(e.target.value)}
                >
                  <option value="all">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Error display */}
          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              {error}
            </div>
          )}
          
          {/* Loading display */}
          {loading && (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
              <h3>Loading tickets...</h3>
            </div>
          )}
          
          {/* Tickets Table */}
          {!loading && (
            <>
              {filteredTickets.length === 0 ? (
                <div className="card shadow-sm">
                  <div className="card-body text-center p-5">
                    <h4 className="mb-3">No tickets found</h4>
                    <p className="text-muted mb-0">Try adjusting your filters or search term.</p>
                  </div>
                </div>
              ) : (
                <div className="card shadow-sm">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th scope="col">Ticket Number</th>
                          <th scope="col">Subject</th>
                          <th scope="col">User</th>
                          <th scope="col">Status</th>
                          <th scope="col">Priority</th>
                          <th scope="col">Created</th>
                          <th scope="col">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTickets.map((ticket) => (
                          <tr 
                            key={ticket.id}
                            onClick={() => window.location.href = `/tickets/${ticket.id}`}
                            style={{ cursor: 'pointer' }}
                            className="ticket-row"
                          >
                            <td>{ticket.ticket_number || ticket.id}</td>
                            <td>{ticket.subject}</td>
                            <td>
                              <div className="d-flex flex-column">
                                <span>{ticket.user_name}</span>
                                <small className="text-muted">{ticket.user_email}</small>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                                {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                                {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                              </span>
                            </td>
                            <td>{formatDate(ticket.created_at)}</td>
                            <td>{formatDate(ticket.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <UserManagement />
      )}
    </div>
  );
};

export default AdminDashboard; 